import React from 'react';
import Calendar from 'react-calendar';
import moment from 'moment';
import styles from './SelectTimeFrame.module.scss';
import 'react-calendar/dist/Calendar.css';

import { useAppDispatch } from '../../store/store';
import editIcon from '../../assets/icons/edit-icon.svg';
import { getCustomerAttachments } from '../../store/slices/documents';
import closeIcon from '../../assets/icons/close.svg';
import { toast } from 'react-toastify';
import { useTranslation } from '../../hooks/useTranslations';

type ValuePiece = Date | null;

type Value = ValuePiece | [ValuePiece, ValuePiece] | null;

type Props = {
  onClose: () => void;
  group_info: { groupName: string; groupId: number } | undefined;
  document_type: string;
  customer_person_id: number | undefined;
};

export default function SelectTimeFrame({
  onClose,
  group_info,
  document_type,
  customer_person_id,
}: Props) {
  const { translations } = useTranslation();

  const frameTypes = [
    { label: translations("today"), name: "today" },
    { label: translations("yesterday"), name: "yesterday" },
    { label: translations("last7days"), name: "last7days" },
    { label: translations("last30days"), name: "last30days" },
    { label: translations("last3months"), name: "last3months" },
    { label: translations("custom"), name: "custom" },
  ];

  const dispatch = useAppDispatch();
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'day').startOf('day');
  const last7DaysStart = moment().subtract(6, 'days').startOf('day');
  const last30DaysStart = moment().subtract(29, 'days').startOf('day');
  const last3MonthsStart = moment().subtract(89, 'days').startOf('day');

  const [selectTimeFrame, setSelectTimeFrame] = React.useState<string>('today');
  const [value, onChange] = React.useState<Value>([today.toDate(), today.toDate()]);

  const handleSelectTimeFrame = (e: any) => {
    setSelectTimeFrame(e.target.name);
    let selectedDate: Value = today.toDate();
    switch (e.target.name) {
      case 'today':
        selectedDate = [today.toDate(), today.toDate()];
        break;
      case 'yesterday':
        selectedDate = [yesterday.toDate(), yesterday.toDate()];
        break;
      case 'last7days':
        selectedDate = [last7DaysStart.toDate(), today.toDate()];
        break;
      case 'last30days':
        selectedDate = [last30DaysStart.toDate(), today.toDate()];
        break;
      case 'last3months':
        selectedDate = [last3MonthsStart.toDate(), today.toDate()];
        break;
      case 'custom':
        selectedDate = null;
        break;
      default:
        selectedDate = today.toDate();
        break;
    }
    onChange(selectedDate);
  };

  const handleSubmit = async () => {
    if (value) {
      const results = await dispatch(
        getCustomerAttachments({
          params: {
            date_from: Array.isArray(value) ? moment(value[0]).format('YYYY-MM-DD') : '',
            date_to: Array.isArray(value) ? moment(value[1]).format('YYYY-MM-DD') : '',
            group_info: group_info,
          },
          document_type,
          customer_person_id,
        })
      );
      if (results.type.includes('rejected')) {
        toast.error(translations('error.no_label_available'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    }
  };
  const CloseButton = () => {
    setSelectTimeFrame('today');
    onClose();
  };

  return (
    <div>
      <div className={styles.container}>
        <div className={styles.header}>
          <div />
          <span>
            {selectTimeFrame !== "custom"
              ? translations("select_time_frame")
              : translations("custom")}
          </span>
          <img
            className={styles.closeButton}
            src={closeIcon}
            alt="closeIcon"
            onClick={CloseButton}
          />
        </div>
        {selectTimeFrame !== "custom" ? (
          <div className={styles.body}>
            <div className={styles.wrapper}>
              {frameTypes.map((type, idx) => {
                if (type.name === "custom") {
                  return (
                    <div key={idx}>
                      <button
                        className={
                          type.name === selectTimeFrame
                            ? styles.blueBtn
                            : styles.transparentBtn
                        }
                        name={type.name}
                        onClick={handleSelectTimeFrame}
                      >
                        <img
                          src={editIcon}
                          className={styles.editIcon}
                          alt="edit"
                        />
                        {type.label}
                      </button>
                    </div>
                  );
                } else {
                  return (
                    <button
                      key={idx}
                      className={
                        type.name === selectTimeFrame
                          ? styles.blueBtn
                          : styles.transparentBtn
                      }
                      name={type.name}
                      onClick={handleSelectTimeFrame}
                    >
                      {type.label}
                    </button>
                  );
                }
              })}
            </div>
          </div>
        ) : (
          <div className={styles.dateInputs}>
            <div className={styles.textInputWrapper}>
              <div className={styles.inputWrapper}>
                <label>{translations("from")}</label>
                <input
                  type="text"
                  disabled
                  value={
                    Array.isArray(value) && value[0]
                      ? moment(value[0]).format("DD.MM.YYYY")
                      : "dd.mm.yyyy"
                  }
                ></input>
              </div>
              <div className={styles.inputWrapper}>
                <label>{translations("to")}</label>
                <input
                  type="text"
                  disabled
                  value={
                    Array.isArray(value) && value[1]
                      ? moment(value[1]).format("DD.MM.YYYY")
                      : "dd.mm.yyyy"
                  }
                ></input>
              </div>
            </div>
            <div className="custom-calendar-wrapper">
              <Calendar
                onChange={onChange}
                value={value}
                selectRange={true}
                maxDate={
                  Array.isArray(value) && value[0]
                    ? moment(value[0]).add(90, "days").toDate()
                    : today.toDate()
                }
                minDate={
                  Array.isArray(value) && value[0]
                    ? moment(value[0]).subtract(90, "days").toDate()
                    : today.subtract(90, "days").toDate()
                }
                showDoubleView={false}
              />
            </div>
          </div>
        )}

        <div className={styles.footer}>
          <button className={styles.blueBtn} onClick={handleSubmit}>
            {translations("download")}
          </button>
        </div>
      </div>
    </div>
  );

}
